import React, { useState, useEffect } from 'react';

import AppPdfViewer from '../element/PdfViewer';
import { StateLanguage } from '../../languages/config/StateLanguage';
import { MediaDA } from '../../services/admin/media.service';
import { BankStatementLine } from '../../models/bank.statement.model';
import { dateFormat } from '../../scripts/date.time.format.script';
import { formatMoney } from '../../scripts/format.value.script';
import { modalHide } from '../../utils/modal.util';

export interface MatchCandidate {
  id: string
  entry_number: string
  date: string
  amount: number
  description: string
  score: number
  kind?: string
}

export interface UnvalidatedCandidate {
  id: string
  kind: 'invoice' | 'provider-invoice' | 'expense'
  number: string
  amount: number
  date: string | null
  counterpart_name: string
  needs_validation: boolean
  id_document_inbox?: string | null
}

export interface PayableCandidate {
  id: string
  kind: 'provider-invoice' | 'invoice'
  number: string
  amount: number
  date: string | null
  counterpart_name: string
  needs_payment: boolean
  id_document_inbox?: string | null
}

export interface AppModalBankLineMatchProps {
  id_company: string
  id_statement: string
  line: BankStatementLine | null
  candidates: MatchCandidate[]
  loading: boolean
  onMatched: (lineId: string, idEntry: string) => Promise<void>
  paymentSuggestions?: { unvalidated_candidates?: UnvalidatedCandidate[]; payable_candidates?: PayableCandidate[]; receivable_candidates?: PayableCandidate[] }
  onValidateDocument?: (doc: UnvalidatedCandidate) => void
  onPayInvoice?: (doc: PayableCandidate) => void
  onManualEntry?: () => void
}

type UnifiedCandidate = {
  key: string
  action: 'pay' | 'validate' | 'match'
  label: string
  badge: string
  badgeClass: string
  buttonClass: string
  title: string
  subtitle: string
  amount: number
  dateMs: number
  id_document_inbox?: string | null
  raw: any
}

const AppModalBankLineMatch: React.FunctionComponent<AppModalBankLineMatchProps> = ({id_company, id_statement, line, candidates, loading, onMatched, paymentSuggestions, onValidateDocument, onPayInvoice, onManualEntry}) => {
  const {lang} = StateLanguage()
  const [matching, setMatching] = useState(false)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [pdfLoading, setPdfLoading] = useState(false)

  useEffect(() => {
    setPdfUrl(null)
    setPdfLoading(false)
    setMatching(false)
  }, [line])

  const handleMatch = async (idEntry: string) => {
    if (!line || matching) return
    // Transitional: accept _id while backend lot "convention id lignes" is pending.
    // REMOVE the _id fallback once all responses expose `id`.
    const resolvedId = line.id || (line as any)._id
    if (!resolvedId) {
      console.error('[ModalBankLineMatch] line has no id — match blocked', line)
      return
    }
    setMatching(true)
    try {
      await onMatched(resolvedId, idEntry)
      modalHide('modal-bank-line-match')
    } finally {
      setMatching(false)
    }
  }

  const handleViewPdf = async (idDocumentInbox: string) => {
    if (!id_company) return
    setPdfLoading(true)
    try {
      const url = await MediaDA.getDocumentSignedUrl(id_company, idDocumentInbox)
      setPdfUrl(url)
    } catch (_e) {
      setPdfUrl(null)
    } finally {
      setPdfLoading(false)
    }
  }

  const handleClose = () => {
    if (matching) return
    setPdfUrl(null)
    modalHide('modal-bank-line-match')
  }

  // Build unified candidate list
  const lineDateMs = line?.date ? new Date(line.date).getTime() : 0
  const dateProx = (d: any) => d ? Math.abs(new Date(d).getTime() - lineDateMs) : Infinity

  const unified: UnifiedCandidate[] = []

  // Case A — validated unpaid invoices
  for (const doc of (paymentSuggestions?.payable_candidates ?? [])) {
    unified.push({
      key: `pay-${doc.id}`, action: 'pay',
      label: lang.labels.reconcileAutomatically ?? 'Reconcile automatically',
      badge: lang.labels.validated ?? 'Validated', badgeClass: 'badge-light-primary',
      buttonClass: 'btn-primary',
      title: `${doc.counterpart_name} — ${formatMoney(doc.amount)}`,
      subtitle: `${doc.number}${doc.date ? ' · ' + dateFormat(lang.code, doc.date) : ''} · ${doc.kind === 'provider-invoice' ? (lang.labels.provider ?? 'Provider') : (lang.labels.customer ?? 'Customer')}`,
      amount: doc.amount, dateMs: dateProx(doc.date), id_document_inbox: doc.id_document_inbox, raw: doc,
    })
  }

  // Receivable candidates — validated unpaid client invoices (credit lines)
  for (const doc of (paymentSuggestions?.receivable_candidates ?? [])) {
    unified.push({
      key: `pay-recv-${doc.id}`, action: 'pay',
      label: lang.labels.reconcileAndCollect ?? 'Reconcile & collect',
      badge: lang.labels.validated ?? 'Validated', badgeClass: 'badge-light-primary',
      buttonClass: 'btn-primary',
      title: `${doc.counterpart_name} — ${formatMoney(doc.amount)}${(doc as any).is_partial ? ` (${lang.labels.partialCollection ?? 'partial collection'})` : ''}`,
      subtitle: `${doc.number}${doc.date ? ' · ' + dateFormat(lang.code, doc.date) : ''} · ${lang.labels.customer ?? 'Customer'}`,
      amount: doc.amount, dateMs: dateProx(doc.date), id_document_inbox: doc.id_document_inbox, raw: doc,
    })
  }

  // Case C/D — unvalidated documents
  for (const doc of (paymentSuggestions?.unvalidated_candidates ?? [])) {
    unified.push({
      key: `validate-${doc.id}`, action: 'validate',
      label: lang.labels.validateAndReconcile ?? 'Validate & reconcile',
      badge: lang.labels.toValidate ?? 'To validate', badgeClass: 'badge-light-warning',
      buttonClass: 'btn-light-warning',
      title: `${doc.counterpart_name} — ${formatMoney(doc.amount)}`,
      subtitle: `${doc.number}${doc.date ? ' · ' + dateFormat(lang.code, doc.date) : ''} · ${doc.kind === 'provider-invoice' ? (lang.labels.provider ?? 'Provider') : doc.kind === 'expense' ? (lang.labels.expense ?? 'Expense') : (lang.labels.customer ?? 'Customer')}`,
      amount: doc.amount, dateMs: dateProx(doc.date), id_document_inbox: doc.id_document_inbox, raw: doc,
    })
  }

  // Case E — accounting entries / manual match
  for (const c of (candidates ?? [])) {
    unified.push({
      key: `match-${c.id}`, action: 'match',
      label: lang.labels.reconcile ?? 'Reconcile',
      badge: c.kind === 'expense' ? (lang.labels.expense ?? 'Expense') : (lang.labels.entry ?? 'Entry'),
      badgeClass: 'badge-light-info',
      buttonClass: 'btn-light',
      title: `${c.description ?? c.entry_number ?? ''} — ${formatMoney(c.amount ?? 0)}`,
      subtitle: `${c.entry_number ?? ''}${c.date ? ' · ' + dateFormat(lang.code, c.date) : ''}`,
      amount: c.amount ?? 0, dateMs: dateProx(c.date), raw: c,
    })
  }

  // Sort by date proximity (closest first)
  unified.sort((a, b) => a.dateMs - b.dateMs)

  const handleUnifiedAction = (u: UnifiedCandidate) => {
    switch (u.action) {
      case 'pay':      onPayInvoice?.(u.raw); break
      case 'validate': onValidateDocument?.(u.raw); break
      case 'match':    handleMatch(u.raw.id); break
    }
  }

  const candidateList = (
    <>
      { line &&
        <div className="bg-light rounded p-4 mb-5">
          <div className="row">
            <div className="col-md-3">
              <div className="text-muted small">{lang.labels.date}</div>
              <div className="fw-bold">{dateFormat(lang.code, line.date)}</div>
            </div>
            <div className="col-md-5">
              <div className="text-muted small">{lang.labels.description}</div>
              <div className="fw-bold">{line.description || '—'}</div>
            </div>
            <div className="col-md-2">
              <div className="text-muted small">{lang.labels.amount}</div>
              <div className={`fw-bold ${line.amount < 0 ? 'text-danger' : 'text-success'}`}>{formatMoney(line.amount)}</div>
            </div>
            <div className="col-md-2">
              <div className="text-muted small">{lang.labels.reference}</div>
              <div className="fw-bold font-monospace">{line.reference || '—'}</div>
            </div>
          </div>
        </div>
      }
      { loading
        ?
        <div className="text-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{lang.labels.loading}</span>
          </div>
        </div>
        :
        unified.length > 0
        ?
        <div>
          { unified.map((u) => (
            <div key={u.key} className="d-flex justify-content-between align-items-center border rounded p-3 mb-2">
              <div>
                <div className="fw-bold">{u.title}</div>
                <div className="text-muted small">
                  {u.subtitle}{' · '}<span className={`badge ${u.badgeClass}`}>{u.badge}</span>
                </div>
              </div>
              <div className="d-flex align-items-center">
                { u.id_document_inbox &&
                  <button
                    className="btn btn-icon btn-sm btn-light-primary me-2"
                    title={lang.labels.viewReferencePdf ?? 'View document'}
                    onClick={(e) => { e.stopPropagation(); handleViewPdf(u.id_document_inbox!) }}
                  >
                    <i className="bi bi-eye fs-4"></i>
                  </button>
                }
                <button
                  className={`btn btn-sm ${u.buttonClass}`}
                  disabled={matching}
                  onClick={() => handleUnifiedAction(u)}
                >
                  {u.label}
                </button>
              </div>
            </div>
          ))}
        </div>
        :
        <div className="text-center text-muted p-5">
          {lang.labels.noCandidatesFound ?? lang.labels.noCandidateFound ?? 'No candidates found'}
        </div>
      }
    </>
  )

  return (
    <div id="modal-bank-line-match" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className={`modal-dialog modal-dialog-centered ${pdfUrl ? 'modal-xl' : 'modal-lg'}`}>
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{lang.labels.reconciliation ?? 'Reconciliation'}</h2>
            <button type="button" className="btn-close" onClick={handleClose} disabled={matching}></button>
          </div>
          <div className="modal-body">
            <div className="d-flex flex-column flex-lg-row">
              <div className={pdfUrl ? 'flex-lg-row-fluid me-lg-4' : 'w-100'} style={pdfUrl ? {maxWidth: '50%'} : undefined}>
                {candidateList}
              </div>
              { pdfUrl &&
                <div className="flex-lg-row-auto" style={{width: '50%', minHeight: '70vh'}}>
                  <div className="card h-100">
                    <div className="card-header py-2 d-flex justify-content-between align-items-center">
                      <h6 className="m-0">{lang.labels.referencePdf ?? 'PDF'}</h6>
                      <button className="btn btn-sm btn-icon" onClick={() => setPdfUrl(null)}>
                        <i className="bi bi-x fs-4"></i>
                      </button>
                    </div>
                    <div className="card-body p-0" style={{height: '70vh'}}>
                      { pdfLoading
                        ? <div className="d-flex justify-content-center align-items-center h-100"><span className="spinner-border text-primary"></span></div>
                        : <AppPdfViewer pdf={pdfUrl} />
                      }
                    </div>
                  </div>
                </div>
              }
            </div>
          </div>
          <div className="modal-footer">
            { onManualEntry &&
              <button className="btn btn-light-primary me-auto" type="button" disabled={matching} onClick={() => { handleClose(); setTimeout(() => onManualEntry(), 300) }}>
                {lang.labels.manualEntry ?? 'Manual entry'}
              </button>
            }
            <button className="btn btn-secondary" type="button" onClick={handleClose} disabled={matching}>{lang.labels.cancel}</button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AppModalBankLineMatch;
